import { LAYOUT_LIMITS } from '@jerome2/shared';
import { app } from './app.ts';
import { PANEL_MIN } from './layout.ts';
import { activateTab, isMailTab, openTab, tabs, updateTabState, type PaneId, type Tab, type TabsState } from './tabs.ts';

// De gesprekkenlijst als kolom van de schil (desktop, ticket 28083) kiest het tabblad waar een gesprek komt. Een klik op een
// gesprek (a) maakt een chattabblad actief dat dat gesprek al open heeft, in welk paneel ook (eerst het actieve paneel en
// daarin het actieve tabblad); anders (b) zet hij het gesprek in het actieve chattabblad van het actieve paneel, met het
// mailpaneel terug naar het begin; anders (c) opent hij een nieuw chattabblad in het actieve paneel. Nieuw gesprek gebruikt
// het actieve tabblad als dat een leeg chattabblad is, anders een nieuw chattabblad; het eerste bericht maakt het gesprek.
// Een tabblad van een mail (ticket 30961, state/mailTabs.ts) hoort bij die mail: (b) en Nieuw gesprek nemen het niet over,
// ook niet zonder gesprek, en openen dan een nieuw chattabblad; het gesprek over de mail zelf maakt het actief (a).
// Na een keuze in een bestaand tabblad staat de focus in het vak van dat tabblad (`focusKey` in de tabstaat, zie ChatInput),
// zodat typen en plakken bij het gekozen gesprek horen; een nieuw tabblad krijgt de focus vanzelf.
// Onder LIST_MIN ruimte wijkt de lijst tot de strook: op een tabblad zonder chat altijd (`columnCrowded`, besluit b op 28083,
// zodat Mail en Agenda hun breedte houden), op een chattabblad alleen zolang het mailpaneel naast het gesprek open is (ChatScreen).

/** De tabstaat van het mailpaneel bij een ander gesprek: dicht, geen keuze, niet vastgehouden en niet vergroot (ticket 28625); het
 * volgende resultaat opent het weer, naast het gesprek. */
export const MAILS_RESET = { mailsOpen: false, mailId: null, mailsSeq: null, mailsHold: false, mailsMax: false };

/** Onder deze ruimte (de gesprekkenkolom samen met het paneel, of met beide panelen) wijkt de lijst tot de strook. */
export const LIST_MIN = 1240;

/** Vanaf deze ruimte (een chatscherm samen met de gesprekkenkolom) staat het mailpaneel naast het gesprek; daaronder is het een vel:
 * het gesprek houdt 480 px en het paneel 300 px, met de gesprekkenkolom als strook (44 px). Zo staat het er ook op 1440 px met de
 * kolom Gevlagd en de werkers open (ticket 30961; tot en met 2.2.49 was de grens 960 px, het paneel op zijn standaard van 420 px).
 * De kolom Gevlagd laat op een chattabblad zoveel ruimte over (shell/FlaggedColumn.tsx). Onder LIST_MIN wijkt de gesprekkenkolom
 * zolang het mailpaneel naast het gesprek open is (ChatScreen). */
export const SIDE_MIN = PANEL_MIN + LAYOUT_LIMITS.mailPane.min + 44;

/** Of de lijst moet wijken voor een tabblad zonder chat: het actieve tabblad van het actieve paneel is geen chat en kolom en
 * panelen hebben samen minder dan LIST_MIN. Een leeg paneel heeft de ruimte niet nodig. */
export function columnCrowded(s: TabsState, pane: PaneId, width: number): boolean {
  const id = s.panes[pane].activeTabId;
  const tab = id ? s.tabs[id] : undefined;
  return tab !== undefined && tab.kind !== 'chat' && width < LIST_MIN;
}

/** Wat een keuze in de kolom doet: een tabblad actief maken, het gesprek in een tabblad zetten, of een nieuw tabblad openen. */
export type ChatChoice = { kind: 'activate'; tabId: string } | { kind: 'set'; tabId: string } | { kind: 'open'; pane: PaneId };

/** Het actieve tabblad van dit paneel, als het een chattabblad is. */
export function activeChatTab(s: TabsState, pane: PaneId): Tab | null {
  const id = s.panes[pane].activeTabId;
  const t = id ? s.tabs[id] : undefined;
  return t?.kind === 'chat' ? t : null;
}

/** Het gesprek dat de kolom markeert: dat van het actieve chattabblad van het actieve paneel; geen als dat tabblad geen chat of leeg is. */
export function markedChat(s: TabsState, pane: PaneId): string | null {
  const chatId = activeChatTab(s, pane)?.state['chatId'];
  return typeof chatId === 'string' ? chatId : null;
}

/** Een klik op een gesprek in de kolom (zie boven: a, b, c). `pane` is het actieve paneel dat in beeld is. */
export function chooseChat(s: TabsState, chatId: string, pane: PaneId): ChatChoice {
  for (const p of [pane, pane === 'left' ? 'right' : 'left'] as const) {
    const { tabIds, activeTabId } = s.panes[p];
    const order = activeTabId ? [activeTabId, ...tabIds.filter((id) => id !== activeTabId)] : tabIds;
    const open = order.find((id) => s.tabs[id]?.kind === 'chat' && s.tabs[id]?.state['chatId'] === chatId);
    if (open) return { kind: 'activate', tabId: open };
  }
  const active = activeChatTab(s, pane);
  return active && !isMailTab(active) ? { kind: 'set', tabId: active.id } : { kind: 'open', pane };
}

/** Nieuw gesprek in de kolom: het actieve tabblad als dat een leeg chattabblad is (geen tabblad van een mail), anders een nieuw
 * chattabblad. */
export function chooseNewChat(s: TabsState, pane: PaneId): ChatChoice {
  const active = activeChatTab(s, pane);
  return active && !active.state['chatId'] && !isMailTab(active) ? { kind: 'set', tabId: active.id } : { kind: 'open', pane };
}

/** De volgende focusKey van een tabblad: ChatInput zet de focus in het vak zodra die verandert. */
export const nextFocus = (tabId: string): number => {
  const key = tabs.get().tabs[tabId]?.state['focusKey'];
  return (typeof key === 'number' ? key : 0) + 1;
};

/** Een klik op een gesprek in de kolom; geeft het tabblad waar het gesprek nu in beeld staat. */
export function pickChat(chatId: string, pane: PaneId): string {
  const choice = chooseChat(tabs.get(), chatId, pane);
  if (choice.kind === 'open') return openTab('chat', { pane: choice.pane, state: { chatId } });
  if (choice.kind === 'activate') {
    activateTab(choice.tabId);
    updateTabState(choice.tabId, { focusKey: nextFocus(choice.tabId) });
  } else updateTabState(choice.tabId, { chatId, composing: false, ...MAILS_RESET, focusKey: nextFocus(choice.tabId) });
  return choice.tabId;
}

/** Nieuw gesprek in de kolom (ook slepen of plakken van bestanden daar); maakt niets aan op de server en geeft het tabblad. */
export function startChat(pane: PaneId): string {
  const choice = chooseNewChat(tabs.get(), pane);
  if (choice.kind === 'open') return openTab('chat', { pane: choice.pane });
  updateTabState(choice.tabId, { chatId: null, composing: false, ...MAILS_RESET, focusKey: nextFocus(choice.tabId) });
  return choice.tabId;
}

/** Een klik op een gesprek in de werkerskolom (ticket 30961, deel 2); geeft het tabblad waar het gesprek nu in beeld staat. Op de
 * desktop zoals een klik in de gesprekkenkolom (`pickChat`, in het actieve paneel dat in beeld is). Op de telefoon het chattabblad
 * dat het gesprek al open heeft, anders het chattabblad dat Chat onderin opent (het eerste), zoals een keuze in de lijst daar (een
 * tabblad van een mail laat dan de mail los), en alleen zonder chattabblad een nieuw. */
export function showChat(chatId: string): string {
  const s = tabs.get();
  const { narrow, wide } = app.get();
  if (!narrow) return pickChat(chatId, s.paneCount === 2 && wide ? s.activePane : 'left');
  const chats = (Object.values(s.tabs) as Tab[]).filter((t) => t.kind === 'chat');
  const tab = chats.find((t) => t.state['chatId'] === chatId) ?? chats[0];
  if (!tab) return openTab('chat', { pane: 'left', state: { chatId } });
  if (tab.state['chatId'] !== chatId) updateTabState(tab.id, { chatId, composing: false, ...MAILS_RESET, ...(isMailTab(tab) ? { mail: null } : {}) });
  activateTab(tab.id);
  return tab.id;
}
